import { FC, memo } from "react";
import { Theme, themeRootClassMap } from "apps/website/maps/Theme.map";
import { Align } from "apps/website/components/base/Text/Text.map";
import { WithTestID } from "apps/website/types";
import cx from "apps/website/utils/misc/cx";
import { AvailableRotation, rotationMap } from "apps/website/maps/Rotation.map";
import { Gap, gapClassMap } from "apps/website/maps/Flex.map";

import TextSubtitle from "../../../Text/TextSubtitle/TextSubtitle";
import { TextSubtitleVariantsProps } from "../../../Text/TextSubtitle/styles";
import { Color, Whitespace } from "../../../Text/constant";
import Icon, { IIcon } from "../../../Icon/Icon";
import { iconSvgMap } from "../../../Icon/Icon.map";

export const tagIconMap = {
  tick: iconSvgMap.tick,
  filledTag: iconSvgMap.filledTag,
  cross: iconSvgMap.cross,
};

type TIconName = keyof typeof tagIconMap;

interface ITagIcon extends IIcon {
  icon: TIconName
}

export interface ISimpleTag extends WithTestID {
  label:string
  bgColor?: Theme;
  textColor?: Color;
  rotation?: AvailableRotation;
  size?: TextSubtitleVariantsProps["size"];
  whitespace?: Whitespace;
  align?: Align;
  className?: string;
  component?: string
  icon?: ITagIcon
  iconButton?: ITagIcon
  onClickIconButton?: () => void
  gap?: Gap
}

const TrailingIconButton = ({ iconButton, onClickIconButton }:
{ iconButton: ITagIcon, onClickIconButton: () => void }) => (
  <button
    type="button"
    aria-label={iconButton.icon}
    onClick={() => onClickIconButton()}
    data-testid={"simple-tag-icon-button"}
  >
    <Icon {...iconButton}/>
  </button>
);

const SimpleTag: FC<ISimpleTag> = ({
  component,
  label,
  icon,
  iconButton,
  onClickIconButton,
  "data-testid": testId,
  bgColor = "default",
  textColor = "default",
  rotation = 0,
  size = 4,
  className,
  whitespace = "noWrap",
  align = "center",
  gap = 2,
}) => (
  <div
    data-component={component ?? SimpleTag.name}
    className={cx(
      "flex w-fit justify-center items-center p-0.5",
      themeRootClassMap[bgColor],
      rotationMap[rotation as AvailableRotation],
      gapClassMap[gap],
      className,
    )}
    data-testid={testId}
  >
    { icon && <Icon {...icon} /> }

    <TextSubtitle align={align} whitespace={whitespace} size={size} color={textColor}>
      { label }
    </TextSubtitle>

    { iconButton && onClickIconButton && (
      <TrailingIconButton iconButton={iconButton} onClickIconButton={onClickIconButton} />
    ) }
  </div>
);

export default memo(SimpleTag);
